import React, { createContext, useMemo, useState } from "react";
import { ElasticSearchContext } from "@me8eon/elastic_search_data_sources";
import { JwtTokenFn } from "@me8eon/authentication";
import { useJwtToken } from "@me8eon/react_authentication";

export type ExplainData = {
    explainDepth: number;
    full?: boolean
}
export type ExplainUsage = [ExplainData, (e: ExplainData) => void];

export type ExplainContextType = {
    usage: ExplainUsage;
    context: (t: JwtTokenFn) => ElasticSearchContext;
}

export const ExplainContext = createContext<ExplainContextType | undefined>(undefined);


export type ExplainProviderProps = {
    children: React.ReactNode;
    context: (t: JwtTokenFn) => ElasticSearchContext;
};

export function ExplainProvider({ children, context }: ExplainProviderProps) {
    const [data, setData] = useState<ExplainData>({ explainDepth: 3 });
    const usage: ExplainUsage = [data, setData];
    const contextValue: ExplainContextType = useMemo(() => ({ usage, context }), [usage, context]);
    return <ExplainContext.Provider value={contextValue}>{children}</ExplainContext.Provider>;
}

export function useExplainData() {
    const usage = React.useContext(ExplainContext);
    if (!usage) throw new Error("useExplainData must be used within a ExplainProvider");
    return usage.usage;
}

export function useExplainElasticSearchContext(): ElasticSearchContext {
    const usage = React.useContext(ExplainContext);
    const jwtTokenFn = useJwtToken();
    if (!usage) throw new Error("useExplainElasticSearchContext must be used within a ExplainProvider");
    return useMemo(() => usage.context(jwtTokenFn), [usage, jwtTokenFn]);
}